// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useState, useEffect } from 'react';
import { Download, Mail } from 'lucide-react';
import { toast } from '../../lib/buses';
import { rowsToCsv, downloadCsv, requestEntityExport, loadSourceColumns } from '../../lib/backOfficeApi';
import { Input } from '../../ui/Form';
import { Modal } from '../../ui/Modal';
import { WizardFooter } from '../../ui/Wizard';

const slugify = (s) => String(s || 'export').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'export';

// ExportFlowModal — the ONE export experience (format · scope · columns · delivery).
// Real engine: pass `rows` (the in-memory dataset) and it produces a real file honoring
// the operator's choices — CSV/JSON client-side, XLSX/PDF + email are honest unless a
// backend handler is supplied. Surfaces with a real server export pass `onConfirm`
// (it owns the export); surfaces with an emailable backend pass `emailExport`; surfaces
// that can fetch the unfiltered set pass `loadAllRows`.
export const ExportFlowModal = ({ onClose, datasetLabel = 'records', totalRows = 0, presetColumns = [], rows = null, source = null, filters = [], onConfirm, emailExport = null, loadAllRows = null, serverExport = false, formats: formatOptions, initialScope = 'filtered', initialFrom = '', initialTo = '' }) => {
  const [format, setFormat] = useState('csv'); // csv | xlsx | pdf | json
  const [scope, setScope] = useState(initialScope); // filtered | all | range
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const [columns, setColumns] = useState(() => {
    const defaults = {};
    presetColumns.forEach(c => { defaults[c.id] = c.defaultOn !== false; });
    return defaults;
  });
  const [delivery, setDelivery] = useState('download'); // download | email
  const [email, setEmail] = useState('');

  // When a surface names an engine `source`, load the engine's REAL columns so the picker
  // for the server scopes (all/range) reflects exactly what the server can return — not the
  // surface's display presets, which may include computed columns the engine doesn't have.
  const [engineCols, setEngineCols] = useState(null);
  const [engineSel, setEngineSel] = useState({});
  useEffect(() => {
    if (!source) return; let alive = true;
    loadSourceColumns(source).then(cols => { if (!alive || !cols.length) return; setEngineCols(cols); setEngineSel(Object.fromEntries(cols.map(c => [c.id, true]))); });
    return () => { alive = false; };
  }, [source]);

  // The server scopes export the engine's columns; the current view exports the surface's.
  const useEngineCols = !!(engineCols && engineCols.length && (scope === 'all' || scope === 'range'));
  const activeCols = useEngineCols ? engineCols : presetColumns;
  const activeSel = useEngineCols ? engineSel : columns;
  const setActiveSel = useEngineCols ? setEngineSel : setColumns;

  const toggleCol = (id) => setActiveSel(c => ({ ...c, [id]: !c[id] }));
  const selectAllCols = () => setActiveSel(Object.fromEntries(activeCols.map(c => [c.id, true])));
  const clearAllCols = () => setActiveSel(Object.fromEntries(activeCols.map(c => [c.id, false])));
  const colsOn = activeCols.filter(c => activeSel[c.id]).length;

  // Capabilities — only offer what's actually backed. Download-of-current-view is always
  // possible client-side from `rows`. "All records" needs a server export (the browser only
  // holds the loaded page); "Email a link" cannot be done client-side at all. So those two are
  // gated on a real backend handler being supplied, instead of silently degrading to the page.
  // Only count capabilities that can genuinely produce MORE than the loaded page. A generic
  // client `onConfirm` (a current-view dump) is NOT all-records and cannot email — trusting it
  // here is the silent-degradation bug. `source` (the report engine) runs the full query;
  // `loadAllRows` fetches the unfiltered set; `serverExport` flags an onConfirm that performs a
  // real server-side all-records export + email (the per-event orders path).
  const canAllRecords = !!(source || loadAllRows || serverExport);
  const canEmail = !!(emailExport || serverExport);
  // Self-correct if a caller-less mode is somehow selected.
  if (scope === 'all' && !canAllRecords) setTimeout(() => setScope('filtered'), 0);
  if (delivery === 'email' && !canEmail) setTimeout(() => setDelivery('download'), 0);

  // Honest size hint: the current view exports exactly the loaded rows; 'all' is unknown
  // until the server runs the job, so don't fabricate a multiplier.
  const scopeRows = scope === 'all' && canAllRecords ? null : totalRows;
  const fileSize = scopeRows == null ? null : Math.max(8, Math.round(scopeRows * Math.max(colsOn, 1) * 0.06));

  const formats = formatOptions || [
    { id: 'csv',  label: 'CSV',  sub: 'Excel-compatible · UTF-8' },
    { id: 'xlsx', label: 'XLSX', sub: 'Excel native · multi-sheet' },
    { id: 'pdf',  label: 'PDF',  sub: 'Report layout · for sharing' },
    { id: 'json', label: 'JSON', sub: 'Raw structured data' },
  ];

  const [busy, setBusy] = useState(false);
  const canExport = colsOn > 0 && (scope !== 'range' || (from && to)) && (delivery !== 'email' || /\S+@\S+/.test(email));

  const confirm = async () => {
    const label = format.toUpperCase();
    const selectedIds = Object.keys(columns).filter(k => columns[k]);
    const params = { format, scope, from, to, columns: selectedIds, delivery, email };

    // The SCOPE chooses the mechanism, not prop precedence:
    //  • 'filtered' (current view) — the in-memory `rows` already reflect the page's live
    //    filters, so the surface's own exporter (onConfirm) or the generic client path
    //    serializes exactly what's on the page: instant, exact, no round-trip.
    //  • 'all' / 'range' — the browser only holds the loaded page, so these MUST run the
    //    server engine (full query → streamed file → ExportJob in the Reports Export
    //    Center). A surface opts into that by naming an engine `source`.
    // So the server branch is tried FIRST for all/range — a surface that also passes a
    // client onConfirm (its current-view dump) doesn't let that swallow all-records.
    // With an engine `source`, the CURRENT-FILTERED view also runs the server engine WITH
    // the page's filters — so it returns the COMPLETE filtered set (the raw-list report
    // for this entity, parameterized by the table's live filters), not just the loaded page.
    // EXCEPT when the surface supplies its OWN onConfirm exporter (it already serializes the
    // exact filtered rows it holds — routing 'filtered' to the engine with empty filters
    // would wrongly export ALL records). 'all'/'range' still always run the engine.
    const needsServer = scope === 'all' || scope === 'range' || (scope === 'filtered' && !!source && !onConfirm);
    if (needsServer && source) {
      setBusy(true);
      try {
        // The picker for server scopes is the engine's real columns (engineCols), so the
        // selected keys map 1:1 to engine columns — true subsetting, no silent drop. If the
        // engine column list didn't load, omit columns → the engine exports the full row.
        const cols = engineCols && engineCols.length
          ? engineCols.filter(c => engineSel[c.id]).map(c => c.key)
          : undefined;
        // 'all' ignores the page's filters by design; 'filtered' & 'range' keep them
        // (range also adds from/to) — so the export matches what the operator filtered to.
        const keepFilters = scope !== 'all';
        const { rows: n } = await requestEntityExport({
          source, columns: cols, format, days: 0,
          filters: keepFilters ? filters : [],
          ...(scope === 'range' ? { from, to } : {}),
        });
        const scopeNote = scope === 'range' ? `${from || '…'} → ${to || '…'}` : (scope === 'filtered' ? 'current filters' : 'all records');
        toast(`${datasetLabel} exported`, 'success', `${n ? Number(n).toLocaleString() + ' rows' : 'server export'} · ${scopeNote} · ${label} · in Export Center`);
        onClose();
      } catch (e) {
        toast('Export failed', 'error', e?.message || 'Server export failed');
      } finally { setBusy(false); }
      return;
    }

    // The surface owns its export — either a real server CSV/email across scopes (orders,
    // no engine `source`) or a current-view client dump that knows its own row shape
    // (payouts/transactions).
    //
    // BUT A SERVER EXPORTER CANNOT MEAN "THE ROWS ON THIS PAGE". `serverExport` says this
    // onConfirm goes and queries the backend; the backend does not know what the operator
    // typed in the search box or which tab they are on. So handing it `filtered` produced
    // the whole dataset while the operator had asked for the five rows in front of them —
    // reported, and reproduced: every onConfirm in this codebase destructures `{ delivery,
    // email }` and drops `scope` on the floor, so the choice never reached anything.
    //
    // `filtered` therefore stays where the modal already defines it (the client path below,
    // over the `rows` this surface handed us — which ARE the filtered view, and are what the
    // row count in this dialog was counting all along). Email still goes to onConfirm: only
    // the server can send one. A bespoke client dump (no `serverExport`) is untouched.
    const onConfirmCanServeScope = !(serverExport && scope === 'filtered' && Array.isArray(rows) && delivery !== 'email');
    if (onConfirm && onConfirmCanServeScope) {
      setBusy(true);
      try {
        const summary = await onConfirm(params);
        const target = delivery === 'email' ? ` · emailed to ${email}` : ' · downloaded';
        toast(`${datasetLabel} exported`, 'success', summary || `${scopeRows != null ? scopeRows.toLocaleString() + ' rows' : 'export'} · ${label}${target}`);
        onClose();
      } catch (e) {
        toast('Export failed', 'error', e?.message || 'Backend rejected the export');
      } finally { setBusy(false); }
      return;
    }

    // 2) Current view (or a surface with no engine source): client-side export of the loaded rows.
    if (rows == null) { toast('Export not wired here yet', 'info', 'This surface didn’t supply its data to the exporter'); return; }
    setBusy(true);
    try {
      // Resolve scope → the actual rows. 'filtered' = what's passed (current view);
      // 'all' uses loadAllRows if the surface can fetch the unfiltered set, else falls
      // back to the filtered rows with an honest note.
      let data = Array.isArray(rows) ? rows : [];
      let scopeNote = 'current view';
      if (scope === 'all') {
        if (loadAllRows) { data = (await loadAllRows()) || []; scopeNote = 'all records'; }
        else { scopeNote = 'current view (full-dataset export needs the server export)'; }
      } else if (scope === 'range' && (from || to)) {
        scopeNote = `current view${from ? ` from ${from}` : ''}${to ? ` to ${to}` : ''}`;
      }
      if (!data.length) { toast('Nothing to export', 'info', `No ${datasetLabel} in ${scopeNote}`); return; }

      // Selected columns → [{key,label}] (fall back to all scalar keys when none preset).
      const cols = presetColumns.length
        ? presetColumns.filter(c => columns[c.id]).map(c => ({ key: c.key || c.id, label: c.label || c.id }))
        : null;

      if (format === 'csv') {
        downloadCsv(rowsToCsv(data, cols), `${slugify(datasetLabel)}.csv`);
      } else if (format === 'json') {
        const projected = cols ? data.map(r => Object.fromEntries(cols.map(c => [c.label, r?.[c.key]]))) : data;
        const blob = new Blob([JSON.stringify(projected, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob); const a = document.createElement('a');
        a.href = url; a.download = `${slugify(datasetLabel)}.json`; document.body.appendChild(a); a.click();
        document.body.removeChild(a); setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else {
        // XLSX/PDF need a generator/library not available client-side. Stay honest and
        // keep the modal open so the operator can pick CSV/JSON.
        toast(`${label} export isn’t available here`, 'info', 'Use CSV or JSON — XLSX/PDF need the reporting service');
        return;
      }

      if (delivery === 'email' && !emailExport) {
        toast('Downloaded instead of emailed', 'info', 'Emailing a link needs the export service — the file downloaded to you');
      } else {
        toast(`${datasetLabel} exported`, 'success', `${data.length.toLocaleString()} rows · ${label} · downloaded`);
      }
      onClose();
    } catch (e) {
      toast('Export failed', 'error', e?.message || 'Could not build the export');
    } finally { setBusy(false); }
  };

  return (
    <Modal open={true} onClose={onClose} width="max-w-[640px]" title={`Export ${datasetLabel}`} sub="Choose format, scope, and the columns to include. Heavy exports are emailed.">
      <div className="space-y-4">
        {/* Format */}
        <div>
          <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Format</div>
          <div className="grid grid-cols-4 gap-2">
            {formats.map(f => (
              <button key={f.id} onClick={() => setFormat(f.id)} className={`text-left rounded-md border px-3 py-2 ${format === f.id ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
                <div className="text-[12px] font-semibold text-[#1F2937]">{f.label}</div>
                <div className="text-[10px] text-[#6B7280] mt-0.5">{f.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Scope */}
        <div>
          <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Scope</div>
          <div className="space-y-1.5">
            <label className={`flex items-start gap-2 px-3 py-2 rounded-md border cursor-pointer ${scope === 'filtered' ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
              <input type="radio" checked={scope === 'filtered'} onChange={() => setScope('filtered')} className="mt-0.5" />
              <div className="flex-1"><div className="text-[12px] font-medium text-[#1F2937]">Current filtered view <span className="font-normal text-[#6B7280]">— {totalRows.toLocaleString()} rows</span></div><div className="text-[10.5px] text-[#6B7280] mt-0.5">{source ? 'Every row matching the current filters · server export (complete, not just the loaded page)' : 'Exactly what you see on the page now'}</div></div>
            </label>
            <label className={`flex items-start gap-2 px-3 py-2 rounded-md border ${!canAllRecords ? 'opacity-50 cursor-not-allowed border-[#E6E4E1]' : scope === 'all' ? 'border-[#1F2937] bg-[#FBFAF7] cursor-pointer' : 'border-[#E6E4E1] hover:bg-[#FBFAF7] cursor-pointer'}`}>
              <input type="radio" checked={scope === 'all'} disabled={!canAllRecords} onChange={() => canAllRecords && setScope('all')} className="mt-0.5" />
              <div className="flex-1"><div className="text-[12px] font-medium text-[#1F2937]">All records <span className="font-normal text-[#6B7280]">— ignores filters</span></div><div className="text-[10.5px] text-[#6B7280] mt-0.5">{canAllRecords ? 'Server export · runs the full query · emailed if large' : 'Needs a server export job — not built for this list yet (only the loaded view can be exported here)'}</div></div>
            </label>
            <label className={`flex items-start gap-2 px-3 py-2 rounded-md border ${!canAllRecords ? 'opacity-50 cursor-not-allowed border-[#E6E4E1]' : scope === 'range' ? 'border-[#1F2937] bg-[#FBFAF7] cursor-pointer' : 'border-[#E6E4E1] hover:bg-[#FBFAF7] cursor-pointer'}`}>
              <input type="radio" checked={scope === 'range'} disabled={!canAllRecords} onChange={() => canAllRecords && setScope('range')} className="mt-0.5" />
              <div className="flex-1">
                <div className="text-[12px] font-medium text-[#1F2937]">Date range <span className="font-normal text-[#6B7280]">{!canAllRecords && '— server export only'}</span></div>
                {canAllRecords && <div className="flex items-center gap-2 mt-1.5">
                  <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-7 px-2 text-[11.5px] border border-[#E6E4E1] rounded bg-white" />
                  <span className="text-[10.5px] text-[#6B7280]">to</span>
                  <input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-7 px-2 text-[11.5px] border border-[#E6E4E1] rounded bg-white" />
                </div>}
              </div>
            </label>
          </div>
        </div>

        {/* Columns — driven by the engine's real columns for server scopes, the surface's
            display columns for the current view, so the picker always matches the output. */}
        {activeCols.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280]">Columns · {colsOn} of {activeCols.length}{useEngineCols ? ' · server fields' : ''}</div>
              <div className="flex items-center gap-2 text-[10.5px]">
                <button onClick={selectAllCols} className="text-[#1F2937] hover:underline">All</button>
                <span className="text-[#D6D3D1]">·</span>
                <button onClick={clearAllCols} className="text-[#1F2937] hover:underline">None</button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-1.5 max-h-[160px] overflow-y-auto border border-[#E6E4E1] rounded-md p-2 bg-[#FBFAF7]">
              {activeCols.map(c => (
                <label key={c.id} className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer hover:bg-white">
                  <input type="checkbox" checked={!!activeSel[c.id]} onChange={() => toggleCol(c.id)} />
                  <span className="text-[11.5px] text-[#1F2937] truncate">{c.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Delivery */}
        <div>
          <div className="text-[10.5px] font-semibold tracking-[0.06em] uppercase text-[#6B7280] mb-1.5">Delivery</div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setDelivery('download')} className={`text-left rounded-md border px-3 py-2 ${delivery === 'download' ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
              <div className="text-[12px] font-semibold text-[#1F2937] inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" />Download now</div>
              <div className="text-[10.5px] text-[#6B7280] mt-0.5">Browser download · best for &lt; 5MB</div>
            </button>
            <button disabled={!canEmail} onClick={() => canEmail && setDelivery('email')} className={`text-left rounded-md border px-3 py-2 ${!canEmail ? 'opacity-50 cursor-not-allowed border-[#E6E4E1]' : delivery === 'email' ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
              <div className="text-[12px] font-semibold text-[#1F2937] inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />Email a link</div>
              <div className="text-[10.5px] text-[#6B7280] mt-0.5">{canEmail ? 'Best for large exports · link expires in 24h' : 'Needs the server export job — not available for this list yet'}</div>
            </button>
          </div>
          {delivery === 'email' && canEmail && (
            <div className="mt-2"><Input value={email} onChange={e => setEmail(e.target.value)} placeholder="ops@tkt.ug" type="email" /></div>
          )}
        </div>

        <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-3 py-2 flex items-center justify-between text-[11.5px]">
          <span className="text-[#6B7280]">Ready to export</span>
          <span className="text-[#1F2937] font-medium tabular-nums">{scopeRows == null ? 'All records · server export' : `${scopeRows.toLocaleString()} rows · ${colsOn} cols · ~${fileSize} KB`}</span>
        </div>
      </div>

      <WizardFooter onCancel={onClose} onNext={confirm} nextLabel={busy ? 'Exporting…' : 'Export now'} nextDisabled={!canExport || busy} primaryIcon={Download} />
    </Modal>
  );
};

// useExport — small hook that returns ({ open, node }) for any list page.
